import { Fragment, type ReactNode } from "react";

type Block =
  | { type: "h1" | "h2" | "h3" | "h4"; text: string }
  | { type: "p"; text: string }
  | { type: "ul" | "ol"; items: string[] }
  | { type: "table"; header: string[]; rows: string[][] }
  | { type: "hr" };

function stripHtmlComments(src: string): string {
  return src.replace(/<!--[\s\S]*?-->/g, "");
}

function parseInline(text: string): ReactNode {
  // Links [text](href), bold **x**, italics *x*, inline code `x`
  const parts: ReactNode[] = [];
  const regex =
    /(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(`([^`]+)`)|(\*([^*]+)\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1]) {
      parts.push(
        <a
          key={key++}
          href={match[3]}
          className="text-salmon font-semibold border-b border-ink/30 hover:border-salmon hover:text-salmon transition-colors"
        >
          {match[2]}
        </a>,
      );
    } else if (match[4]) {
      parts.push(
        <strong key={key++} className="font-serif italic text-ink">
          {match[5]}
        </strong>,
      );
    } else if (match[6]) {
      parts.push(
        <code
          key={key++}
          className="px-1 py-0.5 bg-paper-tan border border-ink/10 rounded text-[0.9em] font-mono"
        >
          {match[7]}
        </code>,
      );
    } else if (match[8]) {
      parts.push(<em key={key++}>{match[9]}</em>);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return (
    <>
      {parts.map((p, i) => (
        <Fragment key={i}>{p}</Fragment>
      ))}
    </>
  );
}

// Exported only so tests/lib/sanityExportFinal.test.ts can pin scripts/sanity-export.mjs renderForm to it.
export function parse(src: string): Block[] {
  const cleaned = stripHtmlComments(src);
  const lines = cleaned.split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    // Horizontal rule
    if (/^---+\s*$/.test(line.trim())) {
      blocks.push({ type: "hr" });
      i++;
      continue;
    }
    // Headings
    const hMatch = /^(#{1,4})\s+(.*)$/.exec(line);
    if (hMatch) {
      const level = hMatch[1].length as 1 | 2 | 3 | 4;
      const type = `h${level}` as "h1" | "h2" | "h3" | "h4";
      blocks.push({ type, text: hMatch[2].trim() });
      i++;
      continue;
    }
    // Unordered list
    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ""));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }
    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }
    // Table (| a | b | then |---|---|)
    if (
      line.trim().startsWith("|") &&
      i + 1 < lines.length &&
      /^\|[-: |]+\|$/.test(lines[i + 1].trim())
    ) {
      const header = line
        .trim()
        .slice(1, -1)
        .split("|")
        .map((c) => c.trim());
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(
          lines[i]
            .trim()
            .slice(1, -1)
            .split("|")
            .map((c) => c.trim()),
        );
        i++;
      }
      blocks.push({ type: "table", header, rows });
      continue;
    }
    // Paragraph: gather until blank/heading/list
    const paraLines: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^#{1,4}\s+/.test(lines[i]) &&
      !/^[-*]\s+/.test(lines[i]) &&
      !/^\d+\.\s+/.test(lines[i]) &&
      !lines[i].trim().startsWith("|")
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push({ type: "p", text: paraLines.join(" ").trim() });
  }
  return blocks;
}

export function renderMarkdown(src: string): ReactNode {
  const blocks = parse(src);
  return blocks.map((b, idx) => {
    switch (b.type) {
      case "h1":
        return (
          <h1
            key={idx}
            className="font-serif text-4xl md:text-6xl tracking-tight leading-[0.95] text-ink mt-8 mb-6"
          >
            {parseInline(b.text)}
          </h1>
        );
      case "h2":
        return (
          <h2
            key={idx}
            className="font-serif text-3xl md:text-4xl tracking-tight text-ink mt-12 mb-4"
          >
            {parseInline(b.text)}
          </h2>
        );
      case "h3":
        return (
          <h3
            key={idx}
            className="font-serif text-2xl tracking-tight text-ink mt-8 mb-2"
          >
            {parseInline(b.text)}
          </h3>
        );
      case "h4":
        return (
          <h4
            key={idx}
            className="text-sm font-bold uppercase tracking-widest text-salmon mt-6 mb-2"
          >
            {parseInline(b.text)}
          </h4>
        );
      case "p":
        return (
          <p
            key={idx}
            className="text-gray-600 leading-relaxed my-5 text-base md:text-lg"
          >
            {parseInline(b.text)}
          </p>
        );
      case "ul":
        return (
          <ul
            key={idx}
            className="list-disc pl-6 space-y-2 my-5 text-gray-600 leading-relaxed marker:text-salmon"
          >
            {b.items.map((it, j) => (
              <li key={j}>{parseInline(it)}</li>
            ))}
          </ul>
        );
      case "ol":
        return (
          <ol
            key={idx}
            className="list-decimal pl-6 space-y-2 my-5 text-gray-600 leading-relaxed marker:text-salmon"
          >
            {b.items.map((it, j) => (
              <li key={j}>{parseInline(it)}</li>
            ))}
          </ol>
        );
      case "table":
        return (
          <div
            key={idx}
            className="my-8 overflow-x-auto rounded-2xl border-2 border-ink/10"
          >
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-paper-tan border-b-2 border-ink/10">
                  {b.header.map((h, j) => (
                    <th
                      key={j}
                      className="text-left py-3 px-4 font-serif text-ink"
                    >
                      {parseInline(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, j) => (
                  <tr key={j} className="border-b border-ink/10 last:border-0">
                    {row.map((cell, k) => (
                      <td key={k} className="py-3 px-4 text-gray-600 align-top">
                        {parseInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case "hr":
        return <hr key={idx} className="my-10 border-ink/15" />;
    }
  });
}
